import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import { useMe } from "@/api/me";

export type AdminTurnEvent = {
  id: string;
  occurred_at: string;
  severity: "error" | "warning" | null;
  kind: string;
  code: string | null;
  text: string;
  detail: string | null;
  session_id: string;
  session_title: string;
  intent: string;
  provider: string;
  owner_email: string;
  org_slug: string;
};

/** Intercom outbox rows that need an operator (spec §11.4): ``stuck``
 *  retries, ``failed`` rows (refused deletions that keep retrying
 *  included) and this process's last failure to set up the custom
 *  attributes. The per-org view leaves out user-level rows. */
export type IntercomSync = {
  stuck: number;
  failed: number;
  cda_error: string | null;
  /** The last milestone hook that failed (superadmins only). */
  hook_error?: string | null;
  /** The worker loop's last failed pass (superadmins only). */
  loop_error?: string | null;
};

/** The Mailchimp product-news sync's rows that need an operator (FW-43):
 *  rows given up (``failed``), erasures still retrying (``stuck``) and the
 *  loop's last failed pass. Sent to superadmins only, where Mailchimp is
 *  configured; null otherwise. */
export type MailchimpSync = {
  stuck: number;
  failed: number;
  /** Erasures given up after a week of retries (part of ``failed``). */
  erase_failed?: number;
  /** The newest failed row's kind and Mailchimp's reason. */
  last_failure?: string | null;
  loop_error: string | null;
};

/** Errors and warnings from agent turns across sessions, org-scoped
 *  like the other admin metrics (``/admin/turn-events``). */
export function useAdminTurnEvents(params: {
  severity: "all" | "error" | "warning";
  hours: number;
  sessionId?: string;
}) {
  const activeOrgSlug = useMe().data?.admin_scope?.active_org_slug ?? null;
  const search = new URLSearchParams({
    severity: params.severity,
    hours: String(params.hours),
  });
  if (params.sessionId) search.set("session_id", params.sessionId);
  return useQuery({
    queryKey: ["admin", "turn-events", params.severity, params.hours, params.sessionId ?? null, activeOrgSlug],
    queryFn: async () =>
      (await api.get<{ items: AdminTurnEvent[]; intercom_sync?: IntercomSync; mailchimp_sync?: MailchimpSync | null }>(
        `/admin/turn-events?${search.toString()}`,
      )).data,
    refetchInterval: 30_000,
  });
}

export type ProviderSetupState = {
  status: "active" | "needs_refresh" | "revoked";
  kind: "oauth" | "api_key";
  expires_at: string | null;
  last_used_at: string | null;
} | null;

export type UserSetupState = Record<string, ProviderSetupState>;

export type OrgSetupState = {
  org_id: string;
  slug: string;
  name: string;
  is_root: boolean;
  members: number;
  members_with_credential: number;
  member_ids: string[];
  code_sources: { count: number; with_read: number; with_write: number; has_default: boolean };
  brand_configured: boolean;
  credits_balance_micros: number | null;
  last_activity_at: string | null;
};

export type AdminSetup = {
  scope: "org" | "platform";
  users: Record<string, UserSetupState>;
  orgs: OrgSetupState[];
};

/** Setup state per org and per user (``/admin/setup``), org-scoped. */
export function useAdminSetup(opts: { scope?: "active" | "all" } = {}) {
  const scope = opts.scope ?? "active";
  const activeOrgSlug = useMe().data?.admin_scope?.active_org_slug ?? null;
  return useQuery({
    queryKey: ["admin", "setup", scope, activeOrgSlug],
    queryFn: async () =>
      (await api.get<AdminSetup>(`/admin/setup?scope=${scope}`)).data,
    staleTime: 30_000,
  });
}

/** One organisation's plan, credits and seats for the superadmin
 *  Organisations list (``GET /admin/org-overview``). ``plan``,
 *  ``agreement`` and ``seats`` are null when billing is off on the
 *  deployment, ``credits`` when the credit ledger is; credits are in
 *  credits, never money. ``seats.capacity`` null means unlimited. */
export type OrgOverviewRow = {
  org_id: string;
  slug: string;
  name: string;
  is_root: boolean;
  members: number;
  /** The latest message in any of the org's sessions, by anyone. */
  last_activity_at: string | null;
  /** When someone other than a platform admin last used the org: the
   *  latest of a turn they ran in it, a member's last request in the app
   *  (pinned to this org or to none) and a member's last sign-in. Null
   *  when no such user ever came. */
  last_accessed_at: string | null;
  plan: { slug: string; name: string; is_internal: boolean } | null;
  agreement: {
    status: string;
    provider: string;
    cancel_at_period_end: boolean;
    current_period_end: string | null;
    scheduled_plan_slug: string | null;
    scheduled_plan_name: string | null;
    scheduled_seat_quantity: number | null;
  } | null;
  seats: { used: number; capacity: number | null } | null;
  credits: { subscription: number; purchased: number; total: number } | null;
};

export type OrgOverview = {
  billing_enabled: boolean;
  credits_enabled: boolean;
  orgs: OrgOverviewRow[];
};

/** Superadmin only; pass ``enabled: false`` for anyone else so the
 *  list never fires a request that 403s. */
export function useAdminOrgOverview(opts: { enabled: boolean }) {
  return useQuery({
    queryKey: ["admin", "org-overview"],
    queryFn: async () =>
      (await api.get<OrgOverview>("/admin/org-overview")).data,
    enabled: opts.enabled,
    staleTime: 30_000,
  });
}
